import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const ValueEngineHero = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Initialize particles
    const initParticles = () => {
      particlesRef.current = [];
      for (let i = 0; i < 60; i++) {
        particlesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2 + 1,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          opacity: Math.random() * 0.4 + 0.1,
        });
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particlesRef.current.forEach((particle, index) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(56, 178, 172, ${particle.opacity})`;
        ctx.fill();

        particlesRef.current.forEach((otherParticle, otherIndex) => {
          if (index !== otherIndex) {
            const dx = particle.x - otherParticle.x;
            const dy = particle.y - otherParticle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 120) {
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              ctx.strokeStyle = `rgba(56, 178, 172, ${0.08 * (1 - distance / 120)})`;
              ctx.lineWidth = 1;
              ctx.stroke();
            }
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    initParticles();
    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const handleValueCheck = () => {
    navigate('/ki-value-check');
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-8">
      {/* Background */}
      <div className="absolute inset-0 bg-brand-hero">
        <canvas ref={canvasRef} className="absolute inset-0" />
        <div className="absolute inset-0 bg-black/10" />
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Text Content */}
          <div className="text-left">
            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              {currentLanguage === 'de' ? (
                <>
                  KI-Transformation mit System:<br />
                  <span className="bg-brand-accent bg-clip-text text-transparent">
                    Die KI-Value-Engine als Garant für Ihren Erfolg.
                  </span>
                </>
              ) : (
                <>
                  AI Transformation with System:<br />
                  <span className="bg-brand-accent bg-clip-text text-transparent">
                    The AI Value Engine as Your Guarantee for Success.
                  </span>
                </>
              )}
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-lg text-ink-muted mb-6 max-w-xl leading-relaxed">
              {currentLanguage === 'de' 
                ? 'Beenden Sie die Experimentierphase. Die KI-Value-Engine ist Ihr steuerbares Betriebsmodell für Portfolio-Steuerung, Value-Dashboarding und DSGVO-konforme Souveränität.'
                : 'End the experimentation phase. The AI Value Engine is your controllable operating model for portfolio management, value dashboarding, and GDPR-compliant sovereignty.'
              }
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Button 
                size="lg"
                onClick={handleValueCheck}
                className="bg-brand hover:bg-brand-600 text-ink-onBrand px-8 py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                {currentLanguage === 'de' ? 'KI-Value-Check starten' : 'Start AI Value Check'}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <a 
                href="/ki-value-engine" 
                className="text-brand-400 hover:text-brand-300 font-medium flex items-center gap-2 transition-colors"
              >
                {currentLanguage === 'de' ? 'Wie die Engine funktioniert' : 'How the Engine works'}
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Right Side - Dashboard Preview */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* CEO Badge - Top Right */}
              <div className="absolute -top-3 -right-3 z-10 bg-gradient-to-r from-amber-500 to-yellow-500 text-ink-onBrand px-3 py-1.5 rounded-full shadow-lg">
                <p className="text-xs font-bold">
                  {currentLanguage === 'de' ? 'CEO-fähig' : 'CEO-ready'}
                </p>
              </div>

              {/* Dashboard Card */}
              <div className="bg-surface-raised/80 backdrop-blur-xl border border-hairline rounded-2xl p-6 shadow-2xl">
                {/* Dashboard Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-white font-semibold text-lg">
                      {currentLanguage === 'de' ? 'KI-Value Dashboard' : 'AI Value Dashboard'}
                    </h3>
                    <p className="text-ink-faint text-sm">
                      {currentLanguage === 'de' ? 'Echtzeit-Übersicht' : 'Real-time overview'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="text-green-400 text-sm">Live</span>
                  </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/[0.05] rounded-xl p-4">
                    <p className="text-ink-faint text-xs mb-1">EBIT Impact</p>
                    <p className="text-2xl font-bold text-white">€1.2M</p>
                    <p className="text-green-400 text-xs">+24% vs. Plan</p>
                  </div>
                  <div className="bg-white/[0.05] rounded-xl p-4">
                    <p className="text-ink-faint text-xs mb-1">ROI</p>
                    <p className="text-2xl font-bold text-white">4.8x</p>
                    <p className="text-green-400 text-xs">Ziel: 3.5x</p>
                  </div>
                  <div className="bg-white/[0.05] rounded-xl p-4">
                    <p className="text-ink-faint text-xs mb-1">Use Cases</p>
                    <p className="text-2xl font-bold text-white">14</p>
                    <p className="text-blue-400 text-xs">8 in Produktion</p>
                  </div>
                  <div className="bg-white/[0.05] rounded-xl p-4">
                    <p className="text-ink-faint text-xs mb-1">Adoption</p>
                    <p className="text-2xl font-bold text-white">78%</p>
                    <p className="text-green-400 text-xs">+12% MoM</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="bg-white/[0.05] rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-ink-faint text-sm">
                      {currentLanguage === 'de' ? 'Jahresfortschritt' : 'Annual Progress'}
                    </span>
                    <span className="text-white font-semibold">68%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-accent rounded-full" style={{ width: '68%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueEngineHero;
